"use client";

import { Clock, Mail, MapPin, Newspaper } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[#45596F]/40 bg-[#45596F]/5">
        <p className="font-default text-sm uppercase tracking-widest text-[#6697B9] mb-3">
          — Transmission Received
        </p>
        <p className="font-default text-2xl text-[#B2BDC7] mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="border-[#B2BDC7]/30 text-[#B2BDC7] hover:bg-[#6697B9] hover:text-[#080F1A] h-12 px-6 font-default uppercase tracking-widest text-xs rounded-lg">
          Initiate New Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-block w-1.5 h-1.5 rounded-lg bg-[#6697B9]" />
        <span className="font-default text-[11px] uppercase tracking-[0.25em] text-[#6697B9] font-medium">
          Establish Uplink
        </span>
      </div>

      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-[#45596F] font-medium">
              Operator Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Commander Name" {...field} className="h-14 bg-transparent border-[#45596F]/20 border-x-0 border-t-0 border-b px-0 text-[#B2BDC7] placeholder:text-[#45596F]/60 font-default text-lg focus-visible:border-[#6697B9] focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-[#45596F] font-medium">
              Secure Channel
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="secure@tialinx.com" {...field} className="h-14 bg-transparent border-[#45596F]/20 border-x-0 border-t-0 border-b px-0 text-[#B2BDC7] placeholder:text-[#45596F]/60 font-default text-lg focus-visible:border-[#6697B9] focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-[#45596F] font-medium">
              Direct Frequency
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (000) 000-0000" {...field} className="h-14 bg-transparent border-[#45596F]/20 border-x-0 border-t-0 border-b px-0 text-[#B2BDC7] placeholder:text-[#45596F]/60 font-default text-lg focus-visible:border-[#6697B9] focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-[#45596F] font-medium">
              Mission Category
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="h-14 w-full bg-transparent border-[#45596F]/20 border-x-0 border-t-0 border-b px-0 text-[#B2BDC7] font-default text-lg data-[placeholder]:text-[#45596F]/60 focus-visible:border-[#6697B9] focus-visible:ring-0">
                  <SelectValue placeholder="Select mission type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-[#080F1A] border-[#45596F]">
                <SelectItem value="Orbital Sensing" className="font-default">
                  Orbital Sensing
                </SelectItem>
                <SelectItem value="Intelligence Data" className="font-default">
                  Intelligence Data
                </SelectItem>
                <SelectItem value="Strategic Partnership" className="font-default">
                  Strategic Partnership
                </SelectItem>
                <SelectItem value="General Inquiry" className="font-default">
                  General Inquiry
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Please provide more detail"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] uppercase tracking-[0.25em] text-[#45596F] font-medium">
              Mission Brief
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Detail your requirements..." {...field} className="min-h-[200px] bg-transparent border-[#45596F] px-4 py-3 text-[#B2BDC7] placeholder:text-[#45596F]/60 font-default text-lg focus-visible:border-[#6697B9] focus-visible:ring-0 resize-none rounded-lg" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="consent" rules={{
      required: "You must agree to be contacted"
    }} render={({
      field
    }) => <FormItem>
            <div className="flex items-start gap-3 pt-2">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-0.5 border-[#45596F]/40 data-[state=checked]:bg-[#6697B9] data-[state=checked]:border-[#6697B9] data-[state=checked]:text-[#080F1A] rounded-lg" />
              </FormControl>
              <Label htmlFor={field.name} className="font-default text-sm text-[#45596F] leading-relaxed cursor-pointer">
                I authorize TiaLinx, Inc. to initiate contact regarding this mission.
              </Label>
            </div>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-900/40 bg-red-900/10 rounded-lg">
          <p className="text-red-400 font-default text-sm">{submitError}</p>
        </div>}

      <div className="pt-4 space-y-4">
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-[#6697B9] text-[#080F1A] hover:bg-[#507697] font-default uppercase tracking-[0.25em] text-xs font-semibold rounded-lg">
          {isSubmitting ? "Transmitting..." : "Transmit Request →"}
        </Button>
        <p className="font-default italic text-[13px] text-[#45596F] text-center">
          — Response protocol: 48-hour window.
        </p>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const infoBlocks = [{
    icon: MapPin,
    label: "Command Center",
    lines: ["TiaLinx Orbital HQ", "Aerospace District, CA"]
  }, {
    icon: Mail,
    label: "LINK CHANNEL",
    lines: ["contact@tialinx.com"],
    isEmail: true
  }, {
    icon: Clock,
    label: "Operational Hours",
    lines: ["Mission Critical Only", "0800 – 1800 PST"]
  }, {
    icon: Newspaper,
    label: "Press/Media",
    lines: ["Public Affairs Office"],
    italic: "media@tialinx.com"
  }];
  return <section data-section-id="5240" id="secure-uplink-terminal" className="bg-[#080F1A] text-[#B2BDC7] relative overflow-hidden py-32 md:py-40 lg:py-[160px] lg:pt-[180px] px-6 md:px-10 lg:px-16">
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-lg bg-[#6697B9]/5 blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-lg bg-[#6697B9]/5 blur-3xl" />
      </div>

     <div className="relative max-w-[1500px] mx-auto px-6 lg:px-8">
 <div className="flex flex-col lg:flex-row lg:items-start gap-16 lg:gap-24">

<div ref={leftRef} className={`w-full lg:w-[56%] xl:w-[58%] overflow-visible transition-all duration-1000 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="flex items-center gap-2 mb-12">
        <span className="inline-block w-2 h-2 rounded-lg bg-[#6697B9]" />
        <span className="font-default text-[11px] uppercase tracking-[0.3em] text-[#45596F] font-medium">
          COMMAND LINK
        </span>
      </div>

      <h1 className="font-default font-extrabold leading-[0.85] tracking-tighter text-[#B2BDC7] mb-10">
        <span className="block text-[clamp(64px,9vw,150px)]">
          SECURE
        </span>

        <span className="block text-[clamp(64px,9vw,150px)]">
          THE{" "}
          <span className="font-default italic font-normal text-[#6697B9] inline-block lg:ml-12">
            SIGNAL.
          </span>
        </span>
      </h1>

      <p className="font-default italic text-xl md:text-[22px] leading-relaxed text-[#45596F] max-w-[460px] mb-16">
        Initiate contact for orbital sensing requirements,
        intelligence data integration, or strategic aerospace
        partnerships.
      </p>
    

            <div className="space-y-0 max-w-[420px]">
              {infoBlocks.map((block, i) => {
              const Icon = block.icon;
              return <div data-index={i} key={i}>
                    {i > 0 && <Separator className="bg-[#45596F]/10" />}
                    <div className="py-6 flex items-start gap-4">
                      <Icon className="size-4 text-[#6697B9] mt-1 shrink-0" />
                      <div className="flex-1">
                        <p className="font-default text-[10px] uppercase tracking-[0.25em] text-[#45596F] font-medium mb-2">
                          {block.label}
                        </p>
                        {block.isEmail ? <Link to="mailto:info@tialinx.com" className="font-default text-lg text-[#B2BDC7] hover:text-[#6697B9] underline underline-offset-[6px] decoration-[#6697B9] decoration-1 transition-colors">
                            {block.lines[0]}
                          </Link> : <div>
                            {block.lines.map((line, j) => <p key={j} className="font-default text-lg text-[#B2BDC7] leading-snug">
                                {line}
                              </p>)}
                            {block.italic && <p className="font-default italic text-base text-[#45596F] mt-1">
                                {block.italic}
                              </p>}
                          </div>}
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div ref={rightRef} className={`w-full lg:w-[44%] xl:w-[42%] flex-shrink-0 transition-all duration-1000 delay-200 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
  <FormProvider formKey="uplink_request_form_contact" formId="uplink_request_form_contact" sectionName="secure-uplink-terminal" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: "",
            consent: false
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {},
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Transmit Request" successMessage="Transmission received. Our command team will process your request within 48 hours." errorMessage="Transmission failure. Please verify your connection and retry." className="border border-[#45596F]/10 bg-[#45596F]/[0.03] backdrop-blur-xl p-8 md:p-12 lg:p-14 rounded-lg">
    <InquiryForm />
  </FormProvider>
</div>
        </div>
      </div>
    </section>;
}